import React from 'react';
import { Ring, Bar, MainMetric, Milestones, ScheduledButton, muted, fmtWhen, fmtDay, openSession, openActivities, openReview, openThread } from './ProgressParts.jsx';

// «Progreso» in a person's card (Personas, the coach only): the same cards and indicators the person sees in Mi
// recorrido (personCard › progress, from personSpace.spaceView, the same rules), with what each one counts, and what
// is pending: next step, coming sessions, her own activities, submissions to review and her conversation. The
// pending items are the same notices as «Por atender»: no new state. Never notes, reflections or message texts.

const card = { background: 'var(--globant-card)', border: '1px solid var(--globant-border)', borderRadius: 16, padding: 20 };
const box = { border: '1px solid var(--globant-border)', borderRadius: 12, padding: 12, display: 'grid', gap: 8 };
const h4 = { margin: 0, fontSize: 11, fontWeight: 800, letterSpacing: 0.8, textTransform: 'uppercase', color: 'var(--globant-muted)' };
const row = { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 13 };
const small = { fontSize: 11, padding: '3px 10px' };
const pill = (tone) => ({ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap', background: tone === 'warn' ? '#FBF0DC' : '#EEF1F0', color: tone === 'warn' ? '#6B4510' : '#33413E' });

export function ServiceCard({ c }) {
  const p = c.progress || {};
  const primary = c.kind === 'diagnostic' ? p.metric : p.sessions;
  const steps = c.kind === 'diagnostic' ? p.stages : p.milestones;
  return (
    <div style={box}>
      <div style={{ ...row, justifyContent: 'space-between' }}><b>{c.name}</b><span style={muted}>{c.type} · {c.statusLabel}</span></div>
      {primary ? <MainMetric metric={primary} /> : <span style={muted}>{c.kind === 'coaching' ? 'Las sesiones se definen al empezar.' : 'Sin etapas todavía.'}</span>}
      {c.kind === 'coaching' && <>
        <Bar metric={p.individual} title="Sesiones individuales" explain />
        <Bar metric={p.activities} title="Actividades" explain />
        <Bar metric={p.goals} title="Objetivos acordados" explain />
      </>}
      {steps?.length > 0 && (
        <details>
          <summary style={{ cursor: 'pointer', fontSize: 13, color: 'var(--globant-green)' }}>Ver el camino ({steps.filter(s => s.state === 'done').length} de {steps.length})</summary>
          <div style={{ marginTop: 8 }}><Milestones steps={steps} title={`Hitos de ${c.name}`} /></div>
        </details>
      )}
    </div>
  );
}

// part: the unified client file shows one part per tab with the same records and states ('cards': progress of each
// service; 'agenda': coming sessions; 'work': activities to do and work to review; 'messages': unread conversations).
// Without it, the whole block as before.
export default function PersonProgress({ v, onChanged, part = null }) {
  const pr = v.progress;
  if (!pr) return null;
  const first = String(v.person?.name || 'la persona').split(' ')[0];
  const tz = v.person?.timeZone;
  const toSchedule = new Set(pr.toSchedule || []);
  const nothing = !pr.cards.length && !pr.courses.length;
  if (part === 'cards') return (
    <section style={card} aria-labelledby="pp-title">
      <div style={{ ...row, justifyContent: 'space-between' }}>
        <h3 id="pp-title" style={{ margin: 0, fontSize: 17 }}>Progreso</h3>
        <span style={{ fontSize: 12, fontWeight: 600, padding: '2px 10px', borderRadius: 999, background: 'var(--teal-dim)', color: '#155C54' }}>Lo mismo que ve {first} en Mi recorrido</span>
      </div>
        <div style={{ flex: '1 1 320px', minWidth: 0, display: 'grid', gap: 10 }}>
          {nothing && <div style={muted}>Sin procesos ni cursos en curso.</div>}
          {pr.cards.map(c => <ServiceCard key={c.key} c={c} />)}
          {pr.courses.map(e => (
            <div key={e.id} style={box}>
              <div style={{ ...row, justifyContent: 'space-between' }}><b>{e.courseName}</b><span style={muted}>Curso</span></div>
              {e.metric ? <MainMetric metric={e.metric} /> : <span style={muted}>Sin lecciones todavía.</span>}
            </div>
          ))}
        </div>
    </section>
  );
  if (part === 'agenda') return (
    <section style={card} aria-label="Agenda">
          <div style={{ display: 'grid', gap: 6 }}>
            <h4 style={h4}>Próximas sesiones</h4>
            {!pr.upcoming.length && <span style={muted}>Ninguna agendada.</span>}
            {pr.upcoming.map(s => (
              <div key={s.id} style={row}>
                <span style={{ fontVariantNumeric: 'tabular-nums', minWidth: 130 }}>{fmtWhen(s.startsAt, tz)}</span>
                <span style={{ flex: '1 1 140px', minWidth: 0 }}>{s.title || `Sesión ${s.number || ''}`}{s.kind === 'group' ? ' (grupal)' : ''}<span style={muted}> · {s.processName}</span></span>
                {toSchedule.has(s.id) && <><span style={pill('warn')}>Reservada por {first} · por agendar</span><ScheduledButton sessionId={s.id} onDone={onChanged} /></>}
                <button type="button" className="action-btn" style={small} onClick={() => openSession(s.processId, s.id)}>Abrir sesión</button>
              </div>
            ))}
          </div>
    </section>
  );
  if (part === 'work') return (
    <section style={card} aria-label="Actividades y entregas">
      <div style={{ display: 'grid', gap: 18 }}>
          <div style={{ display: 'grid', gap: 6 }}>
            <h4 style={h4}>Por revisar · trabajos entregados</h4>
            {!pr.attentionReady && <span style={muted}>No se pudieron leer los pendientes ahora.</span>}
            {pr.attentionReady && !pr.reviews.length && <span style={muted}>Ningún trabajo esperando tu revisión.</span>}
            {pr.reviews.map(r => (
              <div key={r.submissionId} style={row}>
                <span style={pill()}>Entrega</span>
                <span style={{ flex: '1 1 160px', minWidth: 0 }}>{r.lessonTitle}<span style={muted}> · {r.courseName}{r.attempt > 1 ? ` · versión ${r.attempt}` : ''}</span></span>
                <button type="button" className="action-btn btn-primary" style={small} onClick={() => openReview(r)}>Revisar</button>
              </div>
            ))}
            <span style={muted}>Son los mismos pendientes de «Por atender» en Hoy: atenderlos aquí o allá los quita de los dos lados.</span>
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            <h4 style={h4}>Por hacer · actividades de sus procesos</h4>
            {!pr.activities.length && <span style={muted}>Ninguna.</span>}
            {pr.activities.map(a => (
              <div key={a.id} style={row}>
                <span style={{ flex: '1 1 180px', minWidth: 0 }}>{a.title}{a.dueOn ? ` · vence ${fmtDay(a.dueOn)}` : ''}<span style={muted}> · {a.processName}</span></span>
                <button type="button" className="action-btn" style={small} onClick={() => openActivities(a.processId)}>Abrir</button>
              </div>
            ))}
          </div>
      </div>
    </section>
  );
  if (part === 'messages') return (
    <section style={card} aria-label="Mensajes sin leer">
          <div style={{ display: 'grid', gap: 6 }}>
            <h4 style={h4}>Mensajes sin leer</h4>
            {!pr.attentionReady && <span style={muted}>No se pudieron leer los pendientes ahora.</span>}
            {pr.attentionReady && !pr.messages.length && <span style={muted}>Ningún mensaje sin leer.</span>}
            {pr.messages.map(m => (
              <div key={m.id} style={row}>
                <span style={pill()}>Mensajes</span>
                <span style={{ flex: '1 1 160px', minWidth: 0 }}>{m.count} sin leer<span style={muted}> · {m.processName}</span></span>
                <button type="button" className="action-btn" style={small} onClick={() => openThread(m)}>Abrir conversación</button>
              </div>
            ))}
            <span style={muted}>Son los mismos pendientes de «Por atender» en Hoy: atenderlos aquí o allá los quita de los dos lados.</span>
          </div>
    </section>
  );
  return (
    <section style={card} aria-labelledby="pp-title">
      <div style={{ ...row, justifyContent: 'space-between' }}>
        <h3 id="pp-title" style={{ margin: 0, fontSize: 17 }}>Progreso</h3>
        <span style={{ fontSize: 12, fontWeight: 600, padding: '2px 10px', borderRadius: 999, background: 'var(--teal-dim)', color: '#155C54' }}>Lo mismo que ve {first} en Mi recorrido</span>
      </div>
      <p style={{ ...muted, margin: '2px 0 12px' }}>Cada número es «hecho de total» y dice qué cuenta. Sin porcentaje global ni puntajes. Sus reflexiones privadas no aparecen.</p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' }}>
        <div style={{ flex: '1 1 320px', minWidth: 0, display: 'grid', gap: 10 }}>
          {nothing && <div style={muted}>Sin procesos ni cursos en curso.</div>}
          {pr.cards.map(c => <ServiceCard key={c.key} c={c} />)}
          {pr.courses.map(e => (
            <div key={e.id} style={box}>
              <div style={{ ...row, justifyContent: 'space-between' }}><b>{e.courseName}</b><span style={muted}>Curso</span></div>
              {e.metric ? <MainMetric metric={e.metric} /> : <span style={muted}>Sin lecciones todavía.</span>}
            </div>
          ))}
        </div>
        <div style={{ flex: '1 1 280px', minWidth: 0, display: 'grid', gap: 12 }}>
          <div style={{ display: 'grid', gap: 6 }}>
            <h4 style={h4}>Próximas sesiones</h4>
            {!pr.upcoming.length && <span style={muted}>Ninguna agendada.</span>}
            {pr.upcoming.map(s => (
              <div key={s.id} style={row}>
                <span style={{ fontVariantNumeric: 'tabular-nums', minWidth: 130 }}>{fmtWhen(s.startsAt, tz)}</span>
                <span style={{ flex: '1 1 140px', minWidth: 0 }}>{s.title || `Sesión ${s.number || ''}`}{s.kind === 'group' ? ' (grupal)' : ''}<span style={muted}> · {s.processName}</span></span>
                {toSchedule.has(s.id) && <><span style={pill('warn')}>Reservada por {first} · por agendar</span><ScheduledButton sessionId={s.id} onDone={onChanged} /></>}
                <button type="button" className="action-btn" style={small} onClick={() => openSession(s.processId, s.id)}>Abrir sesión</button>
              </div>
            ))}
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            <h4 style={h4}>Actividades pendientes</h4>
            {!pr.activities.length && <span style={muted}>Ninguna.</span>}
            {pr.activities.map(a => (
              <div key={a.id} style={row}>
                <span style={{ flex: '1 1 180px', minWidth: 0 }}>{a.title}{a.dueOn ? ` · vence ${fmtDay(a.dueOn)}` : ''}<span style={muted}> · {a.processName}</span></span>
                <button type="button" className="action-btn" style={small} onClick={() => openActivities(a.processId)}>Abrir</button>
              </div>
            ))}
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            <h4 style={h4}>Por revisar y conversación</h4>
            {!pr.attentionReady && <span style={muted}>No se pudieron leer los pendientes ahora.</span>}
            {pr.attentionReady && !pr.reviews.length && !pr.messages.length && <span style={muted}>Nada esperando.</span>}
            {pr.reviews.map(r => (
              <div key={r.submissionId} style={row}>
                <span style={pill()}>Entrega</span>
                <span style={{ flex: '1 1 160px', minWidth: 0 }}>{r.lessonTitle}<span style={muted}> · {r.courseName}{r.attempt > 1 ? ` · versión ${r.attempt}` : ''}</span></span>
                <button type="button" className="action-btn btn-primary" style={small} onClick={() => openReview(r)}>Revisar</button>
              </div>
            ))}
            {pr.messages.map(m => (
              <div key={m.id} style={row}>
                <span style={pill()}>Mensajes</span>
                <span style={{ flex: '1 1 160px', minWidth: 0 }}>{m.count} sin leer<span style={muted}> · {m.processName}</span></span>
                <button type="button" className="action-btn" style={small} onClick={() => openThread(m)}>Abrir conversación</button>
              </div>
            ))}
            <span style={muted}>Son los mismos pendientes de «Por atender» en Hoy: atenderlos aquí o allá los quita de los dos lados.</span>
          </div>
        </div>
      </div>
    </section>
  );
}

export { Ring };
